import { Close, Payment } from "@mui/icons-material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { Box, Container, Divider, IconButton, ListItemIcon, ListItemText, Typography } from "@mui/material";
import Menu from "@mui/material/Menu";
import { motion } from "framer-motion";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { styleConfig } from "../../config/index";
import { useFloatingBubbles } from "../../context/floating-bubbles/FloatingBubblesContext";
import { useModals } from "../../context/ModalContext";
import { useSocket } from "../../context/SocketContext";
import { pushNotification, updateNotifications } from "../../redux/features/notificationSlice";
import { selectRole } from "../../redux/features/user/selectors/userState.selectors";
import { getBasePath, getMyNotifications, markAsSeenAllMyNotifications, updateMyNotificatioById } from "../../utils";
import { colors } from "../ui/colors";
import { CompanyAvatar } from "../common/CompanyAvatar";
import { AdminInvitationNotificationDialog } from "./AdminInvitationNotificationDialog";
import { useOptionalChatDock } from "../chat-dock";
import { NotificationModals } from "./NotificationModals";
import {
  dedupeNotificationsById,
  ENotificationTargetKind,
  getAccountNotificationDisplayParts,
  getNotificationChatMeta,
  getNotificationChatTarget,
  getInboundMessageSenderCompany,
  getNotificationCompany,
  getNotificationDisplayMessage,
  getNotificationTarget,
  getUniqueNotificationTargets,
  hasNotificationActionFor,
  isInboundMessageNotification,
  NOTIFICATION_ICONS,
  shouldShowNotification,
  triggerNotificationTarget,
} from "./notificationHelpers";

import { NOTIFICATION_STATS_QUERY_KEY, useNotificationStatsQuery } from "./hook/useNotificationStatsQuery";
import { useQueryClient } from "@tanstack/react-query";
import { NOTIFICATION_COUNT_BY_COMPANY_QUERY_KEY } from "@components/common/SidebarConnect/hook/useNotificationCountByCompany";
import { getNotificationCompanyName, useEnsureNotificationCompany } from "./useEnsureNotificationCompany";
import { useDialog } from "../ui/dialogs/useDialog";
import { getModalKeysByStackOrder, saveNotificationReturnState } from "./notificationReturnState";

export const Notification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const notificationsState = useSelector((state) => state.notificationsState);
  const { notifications } = notificationsState;
  const companies = useSelector((state) => state.userState.companies) || [];
  const token = sessionStorage.getItem("token");
  const [selectedAdminInvitationUid, setSelectedAdminInvitationUid] = useState(null);
  const [selectedAdminNotificationId, setSelectedAdminNotificationId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const roleType = useSelector(selectRole);
  const basePath = getBasePath(roleType);
  const { socket } = useSocket();
  const { openCalls } = useFloatingBubbles();
  const chatDock = useOptionalChatDock();
  const { modals } = useModals();
  const { ensureNotificationCompany, shouldSwitchNotificationCompany } = useEnsureNotificationCompany();
  const { showDialog, DialogComponent } = useDialog();
  const activeCompanyId = useSelector((state) => state.userState.activeCompanyId ?? state.userState.company?.id);
  const open = Boolean(anchorEl);
  const visibleNotifications = useMemo(() => dedupeNotificationsById(notifications), [notifications]);
  const menuNotifications = useMemo(() => {
    return visibleNotifications
      .filter((notification) => !notification.isRead)
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [visibleNotifications]);
  const handleViewMoreClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    navigate(`${basePath}/notifications-center`);
  };

  const queryClient = useQueryClient();

  const { data: notificationStats } = useNotificationStatsQuery();

  const notificationsPending = Number(notificationStats?.unseen ?? 0);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await getMyNotifications(token, 1, 20);

        if (!res) return;

        dispatch(
          updateNotifications({
            notifications: dedupeNotificationsById(res?.items ?? []),
          }),
        );
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    fetchNotifications();
  }, [token, dispatch]);

  useEffect(() => {
    if (!socket) return;

    const handleNotification = (data) => {
      try {
        const notification = typeof data === "string" ? JSON.parse(data) : data;

        dispatch(pushNotification(notification));

        queryClient.invalidateQueries({
          queryKey: NOTIFICATION_STATS_QUERY_KEY,
        });

        queryClient.invalidateQueries({
          queryKey: NOTIFICATION_COUNT_BY_COMPANY_QUERY_KEY,
        });
      } catch (error) {
        console.error("[notifications] failed to process event:", error);
      }
    };

    socket.on("notification", handleNotification);

    return () => {
      socket.off("notification", handleNotification);
    };
  }, [socket, dispatch, queryClient]);

  const uniqueTargets = useMemo(() => getUniqueNotificationTargets(visibleNotifications), [visibleNotifications]);
  const handleClick = async (event) => {
    if (anchorEl) {
      handleClose();
      return;
    }

    setAnchorEl(event.currentTarget);

    if (!notificationsPending) {
      return;
    }

    try {
      await markAsSeenAllMyNotifications(token);

      await queryClient.invalidateQueries({
        queryKey: NOTIFICATION_STATS_QUERY_KEY,
      });
    } catch (error) {
      console.error("Error marking notifications as seen:", error);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const markAsRead = (notificationId, e) => {
    if (e) {
      e.stopPropagation();
    }

    const idx = notifications.findIndex((notification) => notification.id === notificationId);
    if (idx === -1) return;

    const updatedNotifications = notifications.map((notification, i) => {
      if (i === idx) {
        return { ...notification, isRead: true };
      }
      return notification;
    });

    dispatch(updateNotifications({ notifications: updatedNotifications }));

    updateMyNotificatioById(sessionStorage.getItem("token"), notifications[idx]?.id, {
      isRead: true,
    })
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: NOTIFICATION_STATS_QUERY_KEY,
        });
      })
      .catch((err) => {
        console.error("Error marking notification as read:", err);

        dispatch(
          updateNotifications({
            notifications,
          }),
        );

        queryClient.invalidateQueries({
          queryKey: NOTIFICATION_STATS_QUERY_KEY,
        });
      });
  };

  const openNotification = async (notification) => {
    const target = getNotificationTarget(notification);
    const chatTarget = getNotificationChatTarget(notification);
    if (!target && !chatTarget) return;

    if (target?.kind === ENotificationTargetKind.AdminInvitation) {
      setSelectedAdminInvitationUid(String(target.id));
      setSelectedAdminNotificationId(notification.id);
      setTimeout(handleClose, 0);
      return;
    }

    try {
      await ensureNotificationCompany(notification);

      if (target && triggerNotificationTarget(target)) {
        markAsRead(notification.id);
        setTimeout(handleClose, 0);
        return;
      }

      if (chatTarget && chatDock) {
        openCalls();
        chatDock.openChat(chatTarget, getNotificationChatMeta(notification, companies));
        markAsRead(notification.id);
        setTimeout(handleClose, 0);
      }
    } catch (error) {
      console.error("Error opening notification:", error);
    }
  };

  const handleAction = async (notification, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (shouldSwitchNotificationCompany(notification)) {
      const companyName = getNotificationCompanyName(notification, companies) || "another company";

      showDialog({
        type: "confirm",
        title: "Switch company?",
        message: `You are about to switch to ${companyName} to open this notification. Are you sure?`,
        confirmLabel: "Yes, switch",
        cancelLabel: "Cancel",
        onCancel: () => {},
        onConfirm: () => {
          saveNotificationReturnState({
            companyId: activeCompanyId,
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
            modalKeys: getModalKeysByStackOrder(modals),
          });
          openNotification(notification);
        },
      });
      return;
    }

    openNotification(notification);
  };

  return (
    <Box sx={{ position: "relative", mr: 1, ml: 1 }}>
      <IconButton
        component={motion.button}
        animate={
          notificationsPending
            ? {
                rotate: [0, -15, 15, -15, 15, 0],
              }
            : {}
        }
        transition={{
          duration: 0.6,
          repeat: Infinity,
          repeatDelay: 3,
        }}
        aria-controls={open ? "notification-positioned-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
      >
        <NotificationsIcon color={notificationsPending ? "warning" : "disabled"} fontSize="large" />
      </IconButton>

      {notificationsPending ? (
        <Box
          sx={{
            borderRadius: "50%",
            width: "24px",
            height: "24px",
            backgroundColor: "#ff4545",
            position: "absolute",
            top: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          component={motion.div}
          animate={{
            rotate: [0, -10, 10, -10, 10, 0, 0, 0],
            transition: { duration: 1.5, repeat: 3 },
          }}
        >
          <Typography variant="h2" sx={{ color: "white", fontSize: 14, fontWeight: "bold" }}>
            {notificationsPending <= 99 ? notificationsPending : 99}
          </Typography>
        </Box>
      ) : null}

      <NotificationModals targets={uniqueTargets} keyPrefix="NotificationBell" />

      <AdminInvitationNotificationDialog
        invitationUid={selectedAdminInvitationUid}
        notificationId={selectedAdminNotificationId}
        onClose={() => {
          setSelectedAdminInvitationUid(null);
          setSelectedAdminNotificationId(null);
        }}
        onMarkAsRead={markAsRead}
      />

      <Menu
        id="demo-positioned-menu"
        aria-labelledby="notification-positioned-button"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        TransitionProps={{ timeout: 0 }}
        slotProps={{
          paper: {
            sx: {
              width: 420,
              maxWidth: "calc(100vw - 32px)",
              borderRadius: "28px",
              boxShadow: "-6px 6px 10px 0px rgba(0, 0, 0, 0.10)",
              border: "1px solid transparent",
            },
          },
        }}
        sx={{
          zIndex: 9999999,
          mt: 6,
          boxShadow: "none",
        }}
      >
        <Container maxWidth={false} sx={{ minHeight: "200px", px: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box sx={{ position: "relative", minWidth: 0, flex: 1 }}>
              <Typography noWrap variant="h5" sx={{ color: colors.orange.base, mt: 2, fontWeight: 500 }}>
                Notifications account
              </Typography>
            </Box>

            <Typography
              component="a"
              sx={{
                color: colors.orange.base,
                textDecoration: "none",
                cursor: "pointer",
                px: 1,
                py: 1,
                flexShrink: 0,
                "&:hover": {
                  color: colors.orange.base,
                },
                mt: 2,
              }}
              onClick={handleViewMoreClick}
            >
              View more
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />
          {menuNotifications.length > 0 ? (
            menuNotifications.slice(0, 3).map((notification) => {
              const displayParts = getAccountNotificationDisplayParts(notification, companies);

              return (
                <Box
                  key={`notification-menu-item-${notification.id}`}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                    p: 1,
                    borderRadius: 1,
                    cursor: hasNotificationActionFor(notification) ? "pointer" : "default",
                    "&:hover": {
                      backgroundColor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.04)",
                    },
                  }}
                  onClick={(e) => {
                    if (hasNotificationActionFor(notification)) {
                      handleAction(notification, e);
                    }
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                    <ListItemIcon sx={{ minWidth: 36, marginLeft: 0, marginRight: 0.75 }}>
                      {getNotificationCompany(notification, companies) || isInboundMessageNotification(notification) ? (
                        <CompanyAvatar
                          company={getNotificationCompany(notification, companies) || getInboundMessageSenderCompany(notification, companies)}
                          size={28}
                        />
                      ) : (
                        (NOTIFICATION_ICONS[notification?.type] ?? <Payment htmlColor="#fc9700" />)
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <>
                          {displayParts.prefix && (
                            <Box component="span" sx={{ fontWeight: 900 }}>
                              {displayParts.prefix}
                            </Box>
                          )}
                          {displayParts.message}
                        </>
                      }
                      sx={{
                        whiteSpace: "normal",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: styleConfig.sidebar.bg,
                        "& .MuiTypography-root": {
                          fontSize: "0.9rem",
                        },
                      }}
                    />
                  </Box>

                  <IconButton
                    size="small"
                    onClick={(e) => markAsRead(notification?.id, e)}
                    sx={{
                      color: "var(--glaucous-hex)",
                      opacity: 0.6,
                      "&:hover": {
                        opacity: 1,
                        backgroundColor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.04)",
                      },
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              );
            })
          ) : (
            <Typography variant="h7" sx={{ color: "var(--glaucous-hex)" }}>
              You currently have no new notifications!
            </Typography>
          )}
        </Container>
      </Menu>
      {DialogComponent}
    </Box>
  );
};
